import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { config } from '../../../config';
import { AuthService, UserRole, normaliseRole } from '../../../auth/auth.service';

interface CVFile {
  name: string;
  size: number;
  uploadedAt: Date;
  uploadedBy?: string;
}

@Component({
  selector: 'app-cv-upload',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cv-upload-section">
      <label class="cv-label">
        CV <span class="required" *ngIf="required">*</span><span class="optional" *ngIf="!required">(Optional)</span>
      </label>
      
      <!-- CV Upload Area -->
      <div class="cv-upload-area" 
           [class.has-file]="selectedFile || existingCvFile"
           [class.drag-over]="isDragOver"
           [class.disabled]="!canManageCV || uploading"
           (dragover)="onDragOver($event)"
           (dragleave)="onDragLeave($event)"
           (drop)="onFileDrop($event)"
           (click)="canManageCV ? fileInput.click() : null">
        
        <!-- Upload State -->
        <div *ngIf="!selectedFile && !existingCvFile" class="upload-prompt">
          <div class="upload-icon-placeholder">CV</div>
          <p>Drag & drop CV here or click to browse</p>
          <p class="file-requirements">Supported: PDF, DOC, DOCX, TXT (Max 5MB)</p>
          <small class="permission-note" *ngIf="showPermissionNote">
            <span *ngIf="userCompanyType === 'MC'">
              MC {{ userRole === 'admin' ? 'Admin' : 'User' }}: You can manage CVs for your company contacts
            </span>
          </small>
        </div>

        
        <!-- Selected File State -->
        <div *ngIf="selectedFile" class="cv-card">
          <div class="cv-header">
            <div class="cv-info">
              <h3 class="cv-title">{{ selectedFile.name }}</h3>
              <p class="upload-date">File size: {{ formatFileSize(selectedFile.size) }}</p>
            </div>
            <div class="cv-header-actions">
              <button type="button" class="btn-icon-header" (click)="viewSelectedFile($event)" title="View">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
              <button type="button" class="btn-icon-header" (click)="removeSelectedFile($event)" title="Remove" [disabled]="uploading">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Existing CV State -->
        <div *ngIf="existingCvFile && !selectedFile" class="cv-card">
          <div class="cv-header">
            <div class="cv-info">
              <h3 class="cv-title">{{ existingCvFile.name }}</h3>
              <p class="upload-date">Uploaded on {{ formatDate(existingCvFile.uploadedAt) }}</p>
            </div>
            <div class="cv-header-actions">
              <button type="button" class="btn-icon-header" (click)="viewCV($event)" title="View">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
              <button type="button" class="btn-icon-header" (click)="downloadCV($event)" title="Download">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
              <button type="button" class="btn-icon-header" (click)="deleteCV($event)" title="Delete" *ngIf="canManageCV">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <div class="cv-update-box">
            <button type="button" class="update-cv-btn" (click)="canManageCV ? fileInput.click() : null; $event.stopPropagation()" [disabled]="!canManageCV">
              Update resume
            </button>
            <p class="format-info">Supported Formats: doc, docx, rtf, pdf, upto 5 MB</p>
          </div>
          
        </div>
        
        <!-- Upload Progress -->
        <div *ngIf="uploading" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" [style.width.%]="uploadProgress"></div>
          </div>
          <span class="progress-text">{{ uploadProgress }}%</span>
        </div>
      </div>
      
      <!-- Hidden File Input -->
      <input #fileInput 
             type="file" 
             accept=".pdf,.doc,.docx,.txt" 
             (change)="onFileSelected($event)" 
             style="display: none;"
             [disabled]="!canManageCV || uploading">
      
      <!-- Validation Error -->
      <div *ngIf="validationError" class="error-message">
        {{ validationError }}
      </div>
    </div>
  `,
  styles: [`
    .cv-upload-section {
      margin-bottom: 20px;
    }

    .cv-label {
      display: block;
      margin-bottom: 6px;
      color: var(--ap-text-soft, #4B5563);
      font-weight: 550;
      font-size: 12px;
    }

    .required {
      color: var(--ap-danger, #B42318);
      margin-left: 2px;
    }

    .optional {
      color: var(--ap-text-muted, #6B7280);
      margin-left: 4px;
      font-size: 12px;
      font-weight: normal;
    }

    .help-text {
      display: block;
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      font-weight: normal;
      margin-top: 2px;
    }

    .cv-upload-area {
      border: 2px dashed var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      padding: 20px;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.15s, background 0.15s;
      background: var(--ap-surface-muted, #EEF0F3);
    }

    .cv-upload-area:hover:not(.disabled) {
      border-color: var(--ap-primary, #2F5BDA);
      background: var(--ap-primary-soft, #E8EEFC);
    }

    .cv-upload-area.drag-over {
      border-color: var(--ap-primary, #2F5BDA);
      background: var(--ap-primary-soft, #E8EEFC);
    }

    .cv-upload-area.has-file {
      border-style: solid;
      border-color: var(--ap-border, #E3E6EB);
      background: var(--ap-surface, #fff);
    }

    .cv-upload-area.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .upload-prompt p {
      margin: 8px 0;
      color: var(--ap-text-soft, #4B5563);
      font-size: 13px;
    }

    .upload-icon-placeholder, .file-icon-placeholder {
      width: 40px;
      height: 40px;
      background: var(--ap-text-muted, #6B7280);
      color: #fff;
      border-radius: var(--ap-radius-sm, 6px);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 10px;
      font-size: 12px;
      font-weight: 600;
    }

    .file-requirements {
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
    }

    .permission-note {
      font-size: 10px;
      color: var(--ap-text-muted, #6B7280);
      font-style: italic;
    }

    .file-selected {
      display: flex;
      align-items: center;
      gap: 12px;
      text-align: left;
    }

    .file-info {
      flex: 1;
    }

    .file-name {
      display: block;
      font-weight: 550;
      color: var(--ap-text, #0B1220);
      word-break: break-all;
      font-size: 13px;
    }

    .file-size, .file-uploaded {
      display: block;
      font-size: 12px;
      color: var(--ap-text-muted, #6B7280);
    }

    .uploaded-by {
      display: block;
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      font-style: italic;
    }

    .remove-file {
      background: var(--ap-danger-soft, #FEE2E2);
      color: var(--ap-danger, #B42318);
      border: none;
      padding: 0 8px;
      height: 28px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 12px;
      font-weight: 550;
      transition: background 0.15s;
    }

    .remove-file:hover:not(:disabled) {
      background: var(--ap-danger, #B42318);
      color: #fff;
    }

    .remove-file:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    /* CV Card Design */
    .cv-card {
      background: var(--ap-surface, #fff);
      padding: 16px;
      margin-top: 8px;
      border-radius: var(--ap-radius-sm, 6px);
    }

    .cv-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 16px;
    }

    .cv-info {
      flex: 1;
      text-align: left;
    }

    .cv-title {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--ap-text, #0B1220);
    }

    .cv-header-actions {
      display: flex;
      gap: 8px;
    }

    .btn-icon-header {
      background: none;
      border: none;
      padding: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--ap-radius-sm, 6px);
      transition: background 0.15s;
    }

    .btn-icon-header:hover {
      background: var(--ap-surface-muted, #EEF0F3);
    }

    .btn-icon-header svg {
      color: var(--ap-text-muted, #6B7280);
    }

    .btn-icon-header:hover svg {
      color: var(--ap-text, #0B1220);
    }

    .cv-file-details {
      margin-bottom: 20px;
    }

    .file-name-display {
      font-size: 15px;
      font-weight: 550;
      color: var(--ap-text, #0B1220);
      margin: 0 0 4px 0;
    }

    .upload-date {
      font-size: 13px;
      color: var(--ap-text-muted, #6B7280);
      margin: 0;
    }

    .cv-update-box {
      border: 2px dashed var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      padding: 20px;
      text-align: center;
      background: var(--ap-surface-muted, #EEF0F3);
      margin-top: 16px;
    }

    .update-cv-btn {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      padding: 0 20px;
      height: 34px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      font-weight: 550;
      cursor: pointer;
      margin-bottom: 10px;
      transition: background 0.15s;
    }

    .update-cv-btn:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    .update-cv-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .format-info {
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      margin: 0;
    }

    .upload-progress {
      margin-top: 10px;
    }

    .progress-bar {
      width: 100%;
      height: 6px;
      background: var(--ap-border, #E3E6EB);
      border-radius: 4px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: var(--ap-primary, #2F5BDA);
      transition: width 0.3s ease;
    }

    .progress-text {
      display: block;
      margin-top: 4px;
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
    }

    .error-message {
      color: var(--ap-danger, #B42318);
      font-size: 12px;
      margin-top: 8px;
    }

    /* Mobile Responsive Styles */
    @media (max-width: 768px) {
      .cv-upload-area {
        padding: 16px;
      }

      .cv-card {
        padding: 12px;
      }

      .cv-header {
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
      }

      .cv-header-actions {
        align-self: flex-end;
        gap: 8px;
      }

      .cv-title {
        font-size: 14px;
      }

      .upload-date {
        font-size: 13px;
      }

      .cv-update-box {
        padding: 16px;
        margin-top: 12px;
      }

      .update-cv-btn {
        padding: 8px 16px;
        font-size: 13px;
        width: 100%;
        max-width: 200px;
      }

      .format-info {
        font-size: 11px;
      }

      .file-selected {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
      }

      .remove-file {
        align-self: flex-end;
        padding: 6px 10px;
        font-size: 11px;
      }

      .upload-prompt p {
        font-size: 14px;
        margin: 6px 0;
      }

      .file-requirements {
        font-size: 10px;
      }

      .btn-icon-header {
        padding: 6px;
      }

      .btn-icon-header svg {
        width: 18px;
        height: 18px;
      }
    }

    @media (max-width: 480px) {
      .cv-upload-area {
        padding: 12px;
      }

      .cv-card {
        padding: 10px;
      }

      .cv-title {
        font-size: 13px;
        word-break: break-word;
      }

      .upload-date {
        font-size: 12px;
      }

      .cv-update-box {
        padding: 12px;
      }

      .update-cv-btn {
        padding: 8px 14px;
        font-size: 12px;
      }

      .format-info {
        font-size: 10px;
        line-height: 1.3;
      }

      .upload-prompt p {
        font-size: 13px;
        margin: 4px 0;
      }

      .file-requirements {
        font-size: 9px;
      }

      .file-name {
        font-size: 13px;
        word-break: break-all;
      }

      .file-size {
        font-size: 11px;
      }

      .cv-header-actions {
        gap: 6px;
      }

      .btn-icon-header svg {
        width: 16px;
        height: 16px;
      }

      .upload-icon-placeholder, .file-icon-placeholder {
        width: 32px;
        height: 32px;
        font-size: 10px;
      }
    }
  `]
})
export class CVUploadComponent implements OnInit {
  @Input() contactId: string | null = null;
  @Input() existingCvFile: CVFile | null = null;
  @Input() required: boolean = false;
  @Input() showPermissionNote: boolean = true;
  
  @Output() cvUploaded = new EventEmitter<CVFile>();
  @Output() cvDeleted = new EventEmitter<void>();
  @Output() fileSelected = new EventEmitter<File>();
  @Output() validationChange = new EventEmitter<string | null>();
  
  selectedFile: File | null = null;
  isDragOver = false;
  uploading = false;
  uploadProgress = 0;
  validationError: string | null = null;
  
  userRole: string = '';
  userCompanyType: string = '';
  canManageCV = false;
  
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}
  
  ngOnInit(): void {
    this.loadUserInfo();
  }
  
  private loadUserInfo(): void {
    const currentUser = this.authService.currentUserValue;
    this.userRole = currentUser?.role || '';
    this.userCompanyType = currentUser?.company_type || '';
    this.canManageCV = this.calculateCVPermissions();
    
  }
  
  private calculateCVPermissions(): boolean {
    // normaliseRole rather than toLowerCase: the role arrives from stored user
    // data, which may still hold a pre-rename string for one token lifetime. An
    // unrecognised role yields null and therefore grants nothing.
    const role = normaliseRole(this.userRole);

    // Platform owner: has no company of its own, so every company comparison
    // below would refuse it. Matches cvFileService.canAccessContactCV on the
    // server - the two must agree, or the UI hides a control the API allows.
    if (UserRole.PLATFORM_OWNER === role) {
      return true;
    }

    // Any recognised role reaches its OWN company's CVs, whatever the company
    // type. This mirrors cvFileService.canAccessContactCV on the server, which
    // is the thing that actually enforces - the two diverging is how the UI came
    // to hide a control the API allows.
    //
    // The enumeration this replaces listed role x company_type pairs, and three
    // live shapes matched none of them: ADMIN and MEMBER of an RC, and any role
    // in a company with no company_type - one of which exists in live data
    // owning contacts. normaliseRole above has already refused anything it
    // cannot interpret, so `role` here is one of the four canonical values.
    return null !== role;
  }
  
  onDragOver(event: DragEvent): void {
    if (!this.canManageCV || this.uploading) return;
    
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }
  
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }
  
  onFileDrop(event: DragEvent): void {
    if (!this.canManageCV || this.uploading) return;
    
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }
  
  onFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }
  
  private handleFileSelection(file: File): void {
    // Validate file
    const validation = this.validateFile(file);
    if (validation) {
      this.validationError = validation;
      this.validationChange.emit(validation);
      return;
    }
    
    this.selectedFile = file;
    this.validationError = null;
    this.validationChange.emit(null);
    this.fileSelected.emit(file);
    
    // Auto-upload if contact ID is available (for existing contacts)
    // For new contacts, just keep the file selected until contact is created
    if (this.contactId) {
      this.uploadCV();
    }
  }
  
  private validateFile(file: File): string | null {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'text/plain'];
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt'];
    
    if (file.size > maxSize) {
      return 'File size must be less than 5MB';
    }
    
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(extension)) {
      return 'Only PDF, DOC, DOCX, and TXT files are allowed';
    }
    
    if (!allowedTypes.includes(file.type) && file.type !== '') {
      return 'Invalid file type';
    }
    
    return null;
  }
  
  async uploadCV(): Promise<void> {
    if (!this.selectedFile || !this.contactId || !this.canManageCV) return;
    
    this.uploading = true;
    this.uploadProgress = 0;
    
    try {
      const formData = new FormData();
      formData.append('cv', this.selectedFile);
      
      const response = await this.http.post<any>(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv`,
        formData,
        {
          reportProgress: true,
          observe: 'events'
        }
      ).toPromise();
      
      this.uploadProgress = 100;
      
      const cvFile: CVFile = {
        name: this.selectedFile.name,
        size: this.selectedFile.size,
        uploadedAt: new Date()
      };
      
      this.existingCvFile = cvFile;
      this.selectedFile = null;
      this.cvUploaded.emit(cvFile);
      
    } catch (error) {
      console.error('Error uploading CV:', error);
      this.validationError = 'Failed to upload CV. Please try again.';
      this.validationChange.emit(this.validationError);
    } finally {
      this.uploading = false;
      this.uploadProgress = 0;
    }
  }
  
  async downloadCV(event: Event): Promise<void> {
    event.stopPropagation();
    
    if (!this.contactId) return;
    
    try {
      const response = await this.http.get(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv`,
        { responseType: 'blob' }
      ).toPromise();
      
      const blob = new Blob([response!], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.existingCvFile?.name || 'cv.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading CV:', error);
      this.validationError = 'Failed to download CV';
    }
  }
  
  async viewCV(event: Event): Promise<void> {
    event.stopPropagation();
    
    if (!this.contactId) return;
    
    try {
      // Check what type of response we get (JSON error or PDF blob)
      const response = await this.http.get(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv/view`,
        { 
          observe: 'response',
          responseType: 'blob'
        }
      ).toPromise();
      
      const contentType = response?.headers.get('Content-Type');
      
      if (contentType?.includes('application/json')) {
        // Parse JSON response (error or document message)
        const text = await response!.body!.text();
        const jsonResponse = JSON.parse(text);
        
        if (jsonResponse.data?.fileType === 'document') {
          alert(jsonResponse.data.message || 'This document type must be downloaded to view.');
          this.downloadCV(event);
          return;
        }
        
        if (!jsonResponse.success) {
          throw new Error(jsonResponse.message || 'Failed to view CV');
        }
      } else if (contentType?.includes('application/pdf')) {
        // PDF blob - open in new tab
        const pdfBlob = response!.body!;
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
        
        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
        return;
      }
      
      // Fallback: open URL directly
      const viewUrl = `${config.apiUrl}/api/contacts/${this.contactId}/cv/view`;
      window.open(viewUrl, '_blank');
      
    } catch (error: any) {
      console.error('Error viewing CV:', error);
      this.validationError = 'Failed to view CV. You can try downloading instead.';
    }
  }
  
  async deleteCV(event: Event): Promise<void> {
    event.stopPropagation();

    if (!this.contactId) return;
    
    try {
      const response = await this.http.delete<any>(
        `${config.apiUrl}/api/contacts/${this.contactId}/cv`
      ).toPromise();
      
      if (response.success) {
        this.existingCvFile = null;
        this.cvDeleted.emit();
      }
      
    } catch (error) {
      console.error('Error deleting CV:', error);
      this.validationError = 'Failed to delete CV';
    }
  }
  
  viewSelectedFile(event: Event): void {
    event.stopPropagation();
    
    if (!this.selectedFile) return;
    
    // Create a blob URL for the selected file
    const fileURL = URL.createObjectURL(this.selectedFile);
    
    // Check if it's a PDF file
    if (this.selectedFile.type === 'application/pdf') {
      // Open PDF in new tab
      window.open(fileURL, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
    } else {
      // For other file types (DOC, DOCX, TXT), show message and offer download
      alert('This file type must be downloaded to view. Would you like to download it?');
      
      // Create download link
      const link = document.createElement('a');
      link.href = fileURL;
      link.download = this.selectedFile.name;
      link.click();
      
      // Clean up blob URL
      URL.revokeObjectURL(fileURL);
    }
  }

  removeSelectedFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.validationError = null;
    this.validationChange.emit(null);
  }
  
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
  
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString();
  }
  
  // Public method to get selected file for form submission
  getSelectedFile(): File | null {
    return this.selectedFile;
  }
  
  // Public method to check if CV is present
  hasCV(): boolean {
    return !!(this.existingCvFile || this.selectedFile);
  }
  
  // Public method to get validation error
  getValidationError(): string | null {
    if (this.required && !this.hasCV()) {
      return 'CV is required';
    }
    return this.validationError;
  }
}